import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChatKeys } from "web_myplann/Api/Chat/ChatKeys";
import {
	appendConfirmedChatMessage,
	appendOptimisticChatMessage,
	markChatMessageFailed,
	removeOptimisticChatMessage,
	replaceOptimisticChatMessage,
} from "web_myplann/Chat/Helpers/chatMessagesCache";
import { createOptimisticChatMessage } from "web_myplann/Chat/Helpers/createOptimisticChatMessage";
import { mapChatMessage } from "web_myplann/Chat/Helpers/mapChatMessage";
import type { ChatMessageView } from "web_myplann/Chat/Types/chatMessage";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	apiPost,
	getApiUrl,
	type PostChatAddImageFetchResponse,
	postChatAddImagePath,
} from "web_myplann/Infrastructure/ApiOld/Api";

type SendImageVariables = {
	conversationId: number;
	file: File;
	content?: string;
	clientId: string;
};

const sendImage = processResponse(
	({ conversationId, file, content }: SendImageVariables) => {
		const formData = new FormData();
		formData.append("conversationId", String(conversationId));
		formData.append("content", content ?? "");
		formData.append("attachment", file);
		formData.append("attachmentTitle", file.name);

		return apiPost(
			`${getApiUrl()}${postChatAddImagePath()}`,
			formData,
			undefined,
		) as Promise<PostChatAddImageFetchResponse>;
	},
);

export const useSendImageMutation = (conversationId: number | null) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: ChatKeys.send(conversationId),
		scope: { id: ChatKeys.sendScope(conversationId) },
		mutationFn: async (
			variables: SendImageVariables,
		): Promise<ChatMessageView | undefined> => {
			const response = await sendImage(variables);
			const item = response?.data?.item;

			return item ? mapChatMessage(item) : undefined;
		},
		onMutate: ({ conversationId, content, clientId, file }) => {
			const localAttachmentUrl = URL.createObjectURL(file);
			const message = createOptimisticChatMessage({
				clientId,
				content: content ?? "",
				file,
				localAttachmentUrl,
			});

			appendOptimisticChatMessage(queryClient, conversationId, message);

			return { conversationId, clientId, localAttachmentUrl };
		},
		onError: (_error, _variables, context) => {
			if (!context) return;
			markChatMessageFailed(
				queryClient,
				context.conversationId,
				context.clientId,
			);
		},
		onSuccess: (message, variables, context) => {
			if (message) {
				const confirmedAttachmentId = message.attachments[0]?.id;
				if (confirmedAttachmentId !== undefined) {
					queryClient.setQueryData(
						ChatKeys.attachmentImage(confirmedAttachmentId),
						variables.file,
					);
				}

				const replaced = replaceOptimisticChatMessage(
					queryClient,
					context.conversationId,
					context.clientId,
					message,
				);

				if (!replaced) {
					appendConfirmedChatMessage(
						queryClient,
						context.conversationId,
						message,
					);
				}
			} else {
				removeOptimisticChatMessage(
					queryClient,
					context.conversationId,
					context.clientId,
				);
				queryClient.invalidateQueries({
					queryKey: ChatKeys.messages(context.conversationId),
				});
			}

			queryClient.invalidateQueries({ queryKey: ChatKeys.list() });
		},
	});
};
